/* eslint-disable import/prefer-default-export */
import AppLogger from '@shared/logging/appLogger';
import {
    Dealer,
    Representative,
    RepresentativeToDealerXref,
    RepresentativeXref2,
} from '@shared/types/dealer-management';
import axios, { AxiosError } from 'axios';
import { v4 } from 'uuid';

export async function getRepresentativeByCiam(ciamId: string, getToken: () => Promise<string>) {
    const representativeUrl = `${process.env.AUCTIONS_GATEWAY_BASEURL}dealer-management/api/representatives/ciam/${ciamId}`;

    const axiosConfig = {
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    };

    return await axios
        .get<Representative>(representativeUrl, axiosConfig)
        .then(response => {
            return response.data;
        })
        .catch((error: AxiosError) => {
            AppLogger.logException({ error: error, properties: { requestUrl: representativeUrl } });
            throw Error(
                `Error getting representative with ciamId:${ciamId}: ${error.response?.status} - ${error.response?.statusText}`
            );
        });
}

export async function getRepresentativeDealerXrefByRepId(
    repId: string,
    getToken: () => Promise<string>
) {
    const representativeUrl = `${process.env.AUCTIONS_GATEWAY_BASEURL}dealer-management/api/representatives/id/${repId}`;

    const axiosConfig = {
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    };

    return await axios
        .get<RepresentativeToDealerXref>(representativeUrl, axiosConfig)
        .then(response => {
            return response.data;
        })
        .catch((error: AxiosError) => {
            AppLogger.logException({ error: error, properties: { requestUrl: representativeUrl } });
            throw Error(
                `Error getting representative with repId:${repId}: ${error.response?.status} - ${error.response?.statusText}`
            );
        });
}

export async function getDealerById(dealerId: string, getToken: () => Promise<string>) {
    const dealerUrl = `${process.env.AUCTIONS_GATEWAY_BASEURL}dealer-management/api/dealers/${dealerId}`;

    const axiosConfig = {
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    };

    return axios
        .get<Dealer>(dealerUrl, axiosConfig)
        .then(response => {
            return response.data;
        })
        .catch((error: AxiosError) => {
            AppLogger.logException({ error: error, properties: { requestUrl: dealerUrl } });
            throw Error(
                `Error getting dealer:${dealerId}: ${error.response?.status} - ${error.response?.statusText}`
            );
        });
}

// dealer-management-v2 API calls

export async function getRepresentativeXrefByCiam(
    ciamId: string,
    getToken: () => Promise<string>
): Promise<RepresentativeXref2> {
    const dealerV2Url = `${process.env.AUCTIONS_GATEWAY_BASEURL}dealer-management-v2/api/v2/xref/ciam/${ciamId}`;

    return await axios
        .get<RepresentativeXref2>(dealerV2Url, {
            headers: {
                Authorization: `Bearer ${await getToken()}`,
                'X-Correlation-ID': v4(),
            },
        })
        .then(response => {
            if (response.status >= 400) {
                const error = new Error(
                    `Error getting dealer xref for ciamId: ${ciamId}: ${response}`
                );
                AppLogger.logException({
                    error: error,
                    properties: { requestUrl: dealerV2Url, response: response },
                });
                throw new Error(`Unable to retrieve dealer xref for ciamId: ${ciamId}`);
            }
            return response.data;
        })
        .catch((err: AxiosError) => {
            const error = new Error(`Unable to retrieve dealer xref for ciamId: ${ciamId}`);
            AppLogger.logException({
                error: error,
                properties: { requestUrl: dealerV2Url, error: err },
            });
            throw err;
        });
}
